import { useEffect, useState } from "react";
import { Swords, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { useUnit } from "../context/UnitContext";
import { DuelCard } from "./DuelCard";
import { TeamDuelSection } from "./TeamDuelSection";

const H2HTable = ({ h2h }) => (
  <div data-testid="h2h-section" className="mt-3 bg-[#14141A] border border-[#2E2E3A] p-3">
    <p className="text-[10px] uppercase tracking-widest text-[#B0B0B4] mb-2">Face-à-face (duels terminés)</p>
    <div className="divide-y divide-white/5">
      {h2h.map((r) => (
        <div key={r.name} data-testid={`h2h-${r.name}`} className="py-1.5 flex items-center justify-between text-xs">
          <span className="text-white font-medium">vs {r.name}</span>
          <span className="font-mono">
            <span className={r.wins > r.losses ? "text-[#32D74B]" : "text-[#ccc]"}>{r.wins} V</span>
            <span className="text-[#555]"> · </span>
            <span className={r.losses > r.wins ? "text-[#FF3B30]" : "text-[#ccc]"}>{r.losses} D</span>
            {r.ties > 0 && <><span className="text-[#555]"> · </span><span className="text-[#B0B0B4]">{r.ties} N</span></>}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const DuelSection = () => {
  const { unit, dw } = useUnit();
  const [duels, setDuels] = useState([]);
  const [h2h, setH2h] = useState([]);
  const [members, setMembers] = useState([]);
  const [exercices, setExercices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [opponent, setOpponent] = useState("");
  const [exercice, setExercice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/duels").then(({ data }) => setDuels(data)).catch(() => {});
    api.get("/duels/h2h").then(({ data }) => setH2h(data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get("/program").then(({ data }) => {
      const names = [...new Set(data.filter((e) => e.muscle !== "Cardio" && e.muscle !== "Mobilité")
        .map((e) => e.exercice))].sort();
      setExercices(names);
    }).catch(() => {});
  }, []);

  const create = async (oppId = opponent, ex = exercice, start = startDate) => {
    if (!oppId || !ex) return;
    setSaving(true);
    try {
      const payload = { opponent_id: oppId, exercice: ex };
      if (start) payload.start_date = start;
      const { data } = await api.post("/duels", payload);
      const scheduled = start && start > new Date().toISOString().slice(0, 10);
      toast.success(scheduled ? `Duel programmé pour le ${start} 📅` : "Duel lancé ! Que le meilleur gagne 🥊", {
        description: data.email_sent ? `Courriel envoyé à ${data.opponent_name}` : undefined,
      });
      setShowForm(false);
      setOpponent("");
      setExercice("");
      setStartDate("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors de la création du duel");
    }
    setSaving(false);
  };

  const rematch = (d) => {
    const other = d.participants.find((p) => !p.is_me);
    if (other) create(other.id, d.exercice, "");
  };

  const remove = async (id) => {
    await api.delete(`/duels/${id}`);
    load();
  };

  return (
    <section data-testid="duel-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-2xl text-white flex items-center gap-2">
          DUELS <Swords size={18} className="text-[#FF3B30]" />
        </h2>
        <button data-testid="duel-new-button" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-xs uppercase tracking-wider border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 px-2.5 py-1.5 transition-colors">
          {showForm ? <X size={13} /> : <Plus size={13} />} {showForm ? "Annuler" : "Lancer un duel"}
        </button>
      </div>

      {showForm && (
        <div data-testid="duel-form" className="bg-[#14141A] border border-[#FF3B30]/40 p-3 mb-3 space-y-2">
          <p className="text-xs text-[#B0B0B4]">1 contre 1 pendant 7 jours · le plus gros volume sur l'exercice gagne</p>
          <select data-testid="duel-opponent-select" value={opponent} onChange={(e) => setOpponent(e.target.value)}
            className="set-input w-full">
            <option value="">Choisis ton adversaire…</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select data-testid="duel-exercice-select" value={exercice} onChange={(e) => setExercice(e.target.value)}
            className="set-input w-full">
            <option value="">Choisis l'exercice…</option>
            {exercices.map((ex) => <option key={ex} value={ex}>{ex}</option>)}
          </select>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B0B0B4] mb-1">Date de début (optionnel — aujourd'hui par défaut)</p>
            <input data-testid="duel-start-date-input" type="date" value={startDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setStartDate(e.target.value)} className="set-input w-full" />
          </div>
          <button data-testid="duel-create-button" onClick={() => create()} disabled={!opponent || !exercice || saving}
            className="w-full h-10 bg-[#FF3B30] text-white font-display text-lg tracking-wide hover:bg-[#e0332a] transition-colors disabled:opacity-30">
            {startDate && startDate > new Date().toISOString().slice(0, 10) ? "PROGRAMMER LE DUEL" : "LANCER LE DUEL"}
          </button>
        </div>
      )}

      {duels.length === 0 && !showForm && (
        <p className="bg-[#14141A] border border-[#2E2E3A] p-3 text-xs text-[#B0B0B4]">
          Aucun duel pour l'instant. Défie un ami sur un exercice pendant une semaine !
        </p>
      )}

      <div className="space-y-2">
        {duels.map((d) => (
          <DuelCard key={d.id} d={d} unit={unit} dw={dw} onDelete={remove} onRematch={rematch} saving={saving} />
        ))}
      </div>

      <TeamDuelSection members={members} exercices={exercices} />

      {h2h.length > 0 && <H2HTable h2h={h2h} />}
    </section>
  );
};
